import { ReactElement, useCallback } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { RESET } from 'jotai/utils';
import { RPCInsufficientInformationException } from 'src/apis/exception/RPCInsufficientInformationException';
import NicknameStep from 'src/components/AzarOnly/Login/Steps/Register/NicknameStep';
import PrimaryLanguageStep from 'src/components/AzarOnly/Login/Steps/Register/PrimaryLanguageStep';
import SmsVerificationPhoneNumberStep from 'src/components/AzarOnly/Login/Steps/SmsVerification/PhoneNumberStep';
import { useGetConsents } from 'src/hooks/useGetConsents';
import { handleAgeRestrictedLoginAttemptAtom } from 'src/stores/auth/atoms';
import { handleNonKmcSelfErrorAtom, pushPhoneNumberLoginHistoryAtom, smsVerificationAtom } from 'src/stores/phoneNumberLogin/atoms';
import { FALLBACK_SIGNUP_SETTINGS, LoginModalType, loginModalTypeAtom, pushSocialRegisterHistoryAtom, registerAtom, signUpSettingsLoadableAtom, socialKmcVerificationAtom } from 'src/stores/register/atoms';
import { UserSettingType } from 'src/stores/user/types';
import ProfileImageStep from './Steps/Register/ProfileImageStep';
const useRegister = () => {
  const loginModalType = useAtomValue(loginModalTypeAtom);
  const pushPhoneNumberLoginHistory = useSetAtom(pushPhoneNumberLoginHistoryAtom);
  const pushSocialRegisterHistory = useSetAtom(pushSocialRegisterHistoryAtom);
  const doRegister = useSetAtom(registerAtom);
  const setSmsVerification = useSetAtom(smsVerificationAtom);
  const setSocialKmcVerification = useSetAtom(socialKmcVerificationAtom);
  const handleNonKmcSelfError = useSetAtom(handleNonKmcSelfErrorAtom);
  const handleAgeRestrictedLoginAttempt = useSetAtom(handleAgeRestrictedLoginAttemptAtom);
  const {
    data: consentsData
  } = useGetConsents();
  const {
    uiType: consentsUiType
  } = consentsData?.data?.result || {};
  const signUpSettingsLoadable = useAtomValue(signUpSettingsLoadableAtom);
  const signUpSettings = signUpSettingsLoadable.state === 'hasData' ? signUpSettingsLoadable.data : FALLBACK_SIGNUP_SETTINGS;
  const pushHistory = useCallback((element: ReactElement) => {
    switch (loginModalType) {
      case LoginModalType.PHONE_NUMBER_LOGIN:
        pushPhoneNumberLoginHistory(element);
        break;
      case LoginModalType.SOCIAL_REGISTER:
        pushSocialRegisterHistory(element);
        break;
      default:
        throw new Error(`Invalid loginModalType - attempted to register from ${loginModalType}`);
    }
  }, [loginModalType, pushPhoneNumberLoginHistory, pushSocialRegisterHistory]);

  /**
   * 현재까지 입력한 정보로 계정 생성을 시도
   * 만약 추가적인 정보 입력이 필요하다면 그에 상응하는 입력 단계로 이동
   */
  const tryRegister = useCallback(async (params: Pick<Parameters<typeof doRegister>[0], 'phoneVerification'> = {}) => {
    const result = await doRegister({
      featureSettings: [{
        type: UserSettingType.HIDE_GENDER,
        value: signUpSettings.genderSettings.hideGenderDefaultValue
      }],
      cmpConsentsAccepted: consentsUiType === 'CONSENT_A',
      ...params
    });
    const [, error] = result;

    // 성공적으로 회원가입 완료
    if (!error) return;
    const handleInsufficientInformationException = (exception: RPCInsufficientInformationException) => {
      switch (exception.data?.throwable.reason) {
        case 'languageAndCountry':
          {
            pushHistory(<PrimaryLanguageStep />);
            break;
          }
        case 'nickname':
          {
            pushHistory(<NicknameStep defaultName={exception.data.throwable.defaultValue} />);
            break;
          }
        case 'profileImage':
          {
            pushHistory(<ProfileImageStep />);
            break;
          }
        default:
          break;
      }
    };
    switch (error.message) {
      case 'AzarIllegalAgeException':
        handleAgeRestrictedLoginAttempt();
        break;
      case 'NotVerifiedException':
        // Social Register의 경우에만 2차 인증 필요한 경우 발생.
        // SMS 경로로 전환하므로 남아있을 수 있는 KMC credential을 지워
        // signup payload에 kmcVerification + phoneVerification이 같이 실리지 않게 한다.
        setSocialKmcVerification(RESET);
        pushHistory(<SmsVerificationPhoneNumberStep />);
        break;
      case 'InsufficientInformationException':
        {
          if ('data' in error) {
            handleInsufficientInformationException(error as RPCInsufficientInformationException);
          }
          break;
        }
      case 'VerificationFailedException':
        // 2차 인증의 인증코드가 틀렸을 경우
        setSmsVerification(_smsVerification => ({
          ..._smsVerification,
          errorMessage: 'SIGNUP_PHONE_VERIFICATION_FAIL'
        }));
        break;
      case 'NotKmcVerifiedException':
        // KMC 자체 에러가 아닌 서버 검증 실패 → 로그인 화면 + 에러 토스트
        handleNonKmcSelfError();
        break;
      default:
        break;
    }
  }, [consentsUiType, doRegister, handleAgeRestrictedLoginAttempt, handleNonKmcSelfError, pushHistory, setSmsVerification, setSocialKmcVerification, signUpSettings.genderSettings.hideGenderDefaultValue]);
  return {
    tryRegister,
    pushHistory
  };
};
export default useRegister;